/* eslint-disable max-len */
import {
  call, put, takeEvery, all, fork, select,
} from 'redux-saga/effects';
import moment from 'moment';
import { api } from '../../helpers/api';
import {
  activeDashboardRequest,
  activeDashboardSuccess,
  fetchDashboardDataRequest,
  fetchDashboardDataSuccess,
  removeChartFromDashboardRequest,
  removeChartFromDashboardSuccess,
  updateDashboardRequest,
  updateDashboardSuccess,
  refetchDashboardDataRequest,
  refetchDashboardDataSuccess,
} from './actions';
import { createToastRequest } from '../toasts/actions';
import {
  refreshRequest,
} from '../auth/actions';
import { ActiveDashboard } from './constants';
import history from '../../history';

const getAuth = (state) => state.auth?.auth;
const getDashboardData = (state) => state.dashboard?.data;

export function* fetchActiveDashboard(ops) {
  const { data, callback } = ops;
  const authData = yield select(getAuth);

  try {
    if (!authData?.access_token) {
      history.push('/login');
    } else {
      const response = yield call(api, `/dashboard?projectId=${data.id}`, 'GET', null, authData.access_token);
      if (response.ok) {
        const parsed = yield response.json();
        if (callback) callback();
        if (parsed?.charts?.length) {
          const chartsWithOrder = parsed.charts.filter(chart => chart.order !== undefined).sort((a, b) => a.order - b.order);
          const chartsWithoutOrder = parsed.charts.filter(chart => chart.order === undefined) || [];
          const sortedCharts = ([...chartsWithOrder, ...chartsWithoutOrder]).map((chart, index) => ({ ...chart, order: index, style: { width: chart?.style?.width || '50%' } }));

          const reqObject = {
            charts: sortedCharts.map((chart) => {
              let timezoneOffset = null;
              let startDate = null;
          
              const rangeParts = chart.range.split(':');
              if (rangeParts.length === 2 && !Number.isNaN(rangeParts[0]) && !Number.isNaN(rangeParts[1])) {
                // Handle unixtime:unixtime format
                const firstPart = parseInt(rangeParts[0], 10);
                const momentDate = moment.unix(firstPart).isValid() ? moment.unix(firstPart) : moment(firstPart);
                if (momentDate.isValid()) {
                  timezoneOffset = momentDate.utcOffset();
                }
              } else {
                // Handle relative time format
                const now = moment();
                if (chart.range.endsWith('d')) {
                  startDate = now.subtract(parseInt(chart.range, 10), 'days');
                } else if (chart.range.endsWith('w')) {
                  startDate = now.subtract(parseInt(chart.range, 10), 'weeks');
                } else if (chart.range.endsWith('M')) {
                  startDate = now.subtract(parseInt(chart.range, 10), 'months');
                }
          
                if (startDate && startDate.isValid()) {
                  timezoneOffset = startDate.utcOffset();
                }
              }
          
              return {
                id: chart.id,
                range: chart.range,
                interval: chart.interval,
                timezoneOffset: timezoneOffset > 0 ? `+${timezoneOffset}` : `-${timezoneOffset}`,
              };
            }),
          };

          yield put(activeDashboardSuccess({ ...parsed, charts: sortedCharts }));

          yield put(fetchDashboardDataRequest(reqObject));
        } else {
          yield put(activeDashboardSuccess(parsed));
        }
      } else if (response.status === 401) {
        yield put(refreshRequest(activeDashboardRequest, data, callback));
      } else {
        const parsed = yield response.json();
        yield put(createToastRequest({ type: 'error', text: parsed?.error, code: parsed?.code }));
      }
    }
  } catch (e) {
    yield put(createToastRequest({ type: 'error', text: 'Unexpected Error' }));
  }
}

export function* fetchDashboardData(ops) {
  const { data, callback } = ops;
  const authData = yield select(getAuth);

  try {
    if (!authData?.access_token) {
      history.push('/login');
    } else {
      const response = yield call(api, '/charts/collect', 'POST', data, authData.access_token);
      if (response.ok) {
        const parsed = yield response.json();
        if (callback) callback();
        yield put(fetchDashboardDataSuccess(parsed));
      } else if (response.status === 401) {
        yield put(refreshRequest(fetchDashboardDataRequest, data, callback));
      } else {
        const parsed = yield response.json();
        yield put(createToastRequest({ type: 'error', text: parsed?.error, code: parsed?.code }));
      }
    }
  } catch (e) {
    yield put(createToastRequest({ type: 'error', text: 'Unexpected Error' }));
  }
}

export function* removeChartFromDashboard(ops) {
  const { data, callback } = ops;
  const { id: chartId, name } = data || {};
  const authData = yield select(getAuth);

  try {
    if (!authData?.access_token) {
      history.push('/login');
    } else {
      const response = yield call(api, `/charts/${chartId}`, 'DELETE', null, authData.access_token);
      if (response.ok) {
        if (callback) callback(true);
        yield put(createToastRequest({ type: 'success', title: name, text: 'chart has been deleted' }));
        yield put(removeChartFromDashboardSuccess(data));
      } else if (response.status === 401) {
        yield put(refreshRequest(removeChartFromDashboardRequest, data, callback));
      } else {
        const parsed = yield response.json();
        if (callback) callback(false);
        yield put(createToastRequest({ type: 'error', text: parsed?.error, code: parsed?.code }));
      }
    }
  } catch (e) {
    if (callback) callback(false);
    yield put(createToastRequest({ type: 'error', text: 'Unexpected Error' }));
  }
}

export function* updateDashboard(ops) {
  const { data, callback } = ops;

  const authData = yield select(getAuth);

  try {
    if (!authData?.access_token) {
      history.push('/login');
    } else {
      yield put(updateDashboardSuccess(data));
      const response = yield call(api, '/dashboard', 'PUT', data, authData.access_token);
      if (response.ok) {
        if (callback) callback();
      } else if (response.status === 401) {
        yield put(refreshRequest(updateDashboardRequest, data, callback));
      } else {
        const parsed = yield response.json();
        yield put(createToastRequest({ type: 'error', text: parsed?.error, code: parsed?.code }));
      }
    }
  } catch (e) {
    if (callback) callback(false);
    yield put(createToastRequest({ type: 'error', text: 'Unexpected Error' }));
  }

};

export function* refetchActiveDashboardData(ops) {
  const { data } = ops;
  const dashboardData = yield select(getDashboardData);

  try {
    // Filter and update charts based on the selected data
    const updatedCharts = dashboardData.charts
      .filter(chart => data.some(d => d.id === chart.id))
      .map(chart => {
        const newData = data.find(d => d.id === chart.id);
        return { ...chart, ...newData };
      });

    const reqObject = {
      charts: updatedCharts.map(chart => {
        let timezoneOffset = null;
        let startDate = null;

        const rangeParts = chart.range.split(':');
        if (rangeParts.length === 2 && !Number.isNaN(rangeParts[0]) && !Number.isNaN(rangeParts[1])) {
          // Handle unixtime:unixtime format
          const firstPart = parseInt(rangeParts[0], 10);
          const momentDate = moment.unix(firstPart).isValid() ? moment.unix(firstPart) : moment(firstPart);
          if (momentDate.isValid()) {
            timezoneOffset = momentDate.utcOffset();
          }
        } else {
          // Handle relative time format
          const now = moment();
          if (chart.range.endsWith('d')) {
            startDate = now.subtract(parseInt(chart.range, 10), 'days');
          } else if (chart.range.endsWith('w')) {
            startDate = now.subtract(parseInt(chart.range, 10), 'weeks');
          } else if (chart.range.endsWith('M')) {
            startDate = now.subtract(parseInt(chart.range, 10), 'months');
          }

          if (startDate && startDate.isValid()) {
            timezoneOffset = startDate.utcOffset();
          }
        }

        return {
          id: chart.id,
          range: chart.range,
          interval: chart.interval,
          timezoneOffset: timezoneOffset > 0 ? `+${timezoneOffset}` : `-${timezoneOffset}`,
        };
      }),
    };

    yield put(fetchDashboardDataRequest(reqObject));
  } catch (e) {
    yield put(createToastRequest({ type: 'error', text: 'Unexpected Error' }));
  }
}

function* activeChartSaga() {
  yield takeEvery(ActiveDashboard.activeDahsboardRequest, fetchActiveDashboard);
  yield takeEvery(ActiveDashboard.fetchDashboardDataRequest, fetchDashboardData);
  yield takeEvery(ActiveDashboard.removeChartFromDashboardRequest, removeChartFromDashboard);
  yield takeEvery(ActiveDashboard.updateDashboardRequest, updateDashboard);
  yield takeEvery(ActiveDashboard.refetchDashboardDataRequest, refetchActiveDashboardData);
}

function* configSaga() {
  yield all([fork(activeChartSaga)]);
}

export default configSaga;
